import { useState, useCallback, useEffect } from 'react';
import { PackageClaimApi } from '@shared/sdk-v1/apis';
import { CourierPackage, PackageClaim, PartnerLink } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';

interface UseUnknownPackageClaimsReturn {
  unknownPackages: CourierPackage[];
  myClaims: PackageClaim[];
  isLoading: boolean;
  error: string | null;
  submitClaim: (
    packageId: number,
    warehouseCourierId: number,
    invoiceFile: string[],
    notes: string
  ) => Promise<void>;
  refresh: () => void;
}

export const useUnknownPackageClaims = (
  partnerLinks: PartnerLink[]
): UseUnknownPackageClaimsReturn => {
  const { user, courier } = useAuth();
  const [unknownPackages, setUnknownPackages] = useState<CourierPackage[]>([]);
  const [myClaims, setMyClaims] = useState<PackageClaim[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activePartnerLinks = partnerLinks.filter((pl) => pl.status === 'active');

  const fetchData = useCallback(async () => {
    if (!user?.id || !courier?.id || activePartnerLinks.length === 0) return;

    setIsLoading(true);
    setError(null);

    const config = apiConfig();
    const claimApi = new PackageClaimApi(config);

    try {
      // Fetch unknown packages from all linked warehouses
      const packagePromises = activePartnerLinks.map((pl) =>
        claimApi
          .getUnknownPackages({
            warehouseCourierId: Number(pl.warehouseTenantId),
          })
          .catch(() => [] as CourierPackage[])
      );

      // Fetch my claims
      const claimsPromise = claimApi
        .getMyClaims({ claimantCourierId: courier.id as number })
        .catch(() => [] as PackageClaim[]);

      const [packagesResults, claimsResult] = await Promise.all([
        Promise.all(packagePromises),
        claimsPromise,
      ]);

      // Flatten all unknown packages from all warehouses
      const allPackages = packagesResults.flat();
      setUnknownPackages(allPackages);
      setMyClaims(claimsResult);
    } catch (err) {
      console.error('Error fetching unknown package data:', err);
      setError('Failed to load unknown packages. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, courier?.id, activePartnerLinks.length]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submitClaim = useCallback(
    async (
      packageId: number,
      warehouseCourierId: number,
      invoiceFile: string[],
      notes: string
    ) => {
      if (!user?.id || !courier?.id) {
        throw new Error('User not authenticated');
      }

      const claimApi = new PackageClaimApi(apiConfig());
      await claimApi.createClaim({
        data: {
          packageId,
          claimantCourierId: courier.id as number,
          claimantUserId: user.id as number,
          warehouseCourierId,
          invoiceFile,
          notes,
        },
      });

      // Refresh data after submitting
      await fetchData();
    },
    [user?.id, courier?.id, fetchData]
  );

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    unknownPackages,
    myClaims,
    isLoading,
    error,
    submitClaim,
    refresh,
  };
};
